import { useEffect } from "react";
import { DEFAULT_META, SITE_NAME, SITE_URL } from "../utils/constants";

const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url);

const toAbsoluteUrl = (url) => {
  if (!url) return "";
  if (isAbsoluteUrl(url)) return url;
  const normalized = url.startsWith("/") ? url : `/${url}`;
  return `${SITE_URL}${normalized}`;
};

const setMetaTag = ({ name, property, content }) => {
  if (!content) return;

  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    if (name) element.setAttribute("name", name);
    if (property) element.setAttribute("property", property);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const setLinkTag = ({ rel, href }) => {
  if (!href) return;

  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

const formatTitle = (pageTitle) => {
  if (!pageTitle) return DEFAULT_META.title;
  if (pageTitle.toLowerCase().includes(SITE_NAME.toLowerCase())) return pageTitle;
  return `${pageTitle} | ${SITE_NAME}`;
};

const Seo = ({
  title = DEFAULT_META.title,
  description = DEFAULT_META.desc,
  keywords = DEFAULT_META.keywords,
  image = DEFAULT_META.image,
  path,
  noIndex = false,
  type = "website",
}) => {
  useEffect(() => {
    const resolvedTitle = formatTitle(title);
    const resolvedDescription = description || DEFAULT_META.desc;
    const resolvedKeywords = keywords || DEFAULT_META.keywords;
    const resolvedUrl = path ? toAbsoluteUrl(path) : `${SITE_URL}${window.location.pathname}`;
    const resolvedImage = toAbsoluteUrl(image || DEFAULT_META.image);

    document.title = resolvedTitle;

    setMetaTag({ name: "description", content: resolvedDescription });
    setMetaTag({ name: "keywords", content: resolvedKeywords });
    setMetaTag({ name: "robots", content: noIndex ? "noindex, nofollow" : "index, follow" });

    setMetaTag({ property: "og:type", content: type });
    setMetaTag({ property: "og:site_name", content: SITE_NAME });
    setMetaTag({ property: "og:title", content: resolvedTitle });
    setMetaTag({ property: "og:description", content: resolvedDescription });
    setMetaTag({ property: "og:url", content: resolvedUrl });
    setMetaTag({ property: "og:image", content: resolvedImage });
    setMetaTag({ property: "og:image:alt", content: `${SITE_NAME} logo` });

    setMetaTag({ name: "twitter:card", content: "summary_large_image" });
    setMetaTag({ name: "twitter:title", content: resolvedTitle });
    setMetaTag({ name: "twitter:description", content: resolvedDescription });
    setMetaTag({ name: "twitter:image", content: resolvedImage });

    setLinkTag({ rel: "canonical", href: resolvedUrl });
  }, [title, description, keywords, image, path, noIndex, type]);

  return null;
};

export default Seo;
